
import type React from 'react';
import { useState, useCallback, type CSSProperties, useImperativeHandle, forwardRef } from 'react'
import SingleInput from './SingleInput';
import { digitsOnly } from '../../helper';

function otpDigitsOnly(str: string): string {
  return digitsOnly(str).replace(/\D/g, '');
}

export type OTPInputProps = {
    length: number;
    onChangeOTP: (otp: string) => void;
    onCompleted?: (otp: string) => void;
    autoFocus?: boolean;
    isNumberInput?: boolean;
    disabled?: boolean;
    inputStyle?: CSSProperties;
    inputClassName?: string;
} & JSX.IntrinsicElements['input']

export type OTPInputRef = {
    reset: () => void;
    focusFirstInput: () => void;

}

export const OTPInput = forwardRef<OTPInputRef, OTPInputProps>((props, ref) => {
    const {
        length,
        isNumberInput,
        autoFocus,
        disabled,
        onChangeOTP,
        onCompleted,
        inputClassName,
        inputStyle,
        ...rest
    } = props;
    void isNumberInput;

    const [activeInput, setActiveInput] = useState(0);
    const [otpValues, setOTPValues] = useState(Array<string>(length).fill(''));
    // Helper to return OTP from inputs
    const handleOtpChange = useCallback(
        (otp: string[]) => {
            const otpValue = otp.join('');
            onChangeOTP(otpValue);
            // Check if all inputs are filled and call onCompleted
            if (otpValue.length === length) {
                onCompleted?.(otpValue);
            }
        },
        [length, onChangeOTP, onCompleted],
    );
    const reset = useCallback(() => {
        setOTPValues(Array<string>(length).fill(''));
        setActiveInput(0);
    }, [length]);
    useImperativeHandle(ref, () => ({
        reset,
        focusFirstInput: () => {
            setActiveInput(0);
        }
    }));
    // Change OTP value at focussing input
    const changeCodeAtFocus = useCallback(
        (str: string) => {
            const updatedOTPValues = [...otpValues];
            updatedOTPValues[activeInput] = str[0] || '';
            setOTPValues(updatedOTPValues);
            handleOtpChange(updatedOTPValues);
        },
        [activeInput, handleOtpChange, otpValues],
    );

    // Focus `inputIndex` input
    const focusInput = useCallback(
        (inputIndex: number) => {
            const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
            setActiveInput(selectedIndex);
        },
        [length],
    );

    const focusPrevInput = useCallback(() => {
        focusInput(activeInput - 1);
    }, [activeInput, focusInput]);


    const focusNextInput = useCallback(() => {
        focusInput(activeInput + 1);
    }, [activeInput, focusInput]);

    // Handle onFocus input
    const handleOnFocus = useCallback(
        (index: number) => () => {
            focusInput(index);
        },
        [focusInput],
    );

    const handleOnPaste = useCallback(
      (e?: React.ClipboardEvent<HTMLInputElement>, otpArrays?: string[]) => {
        e?.preventDefault();

        const pastedData = otpArrays
          ? otpArrays
          : otpDigitsOnly(e?.clipboardData?.getData('text/plain')?.trim() ?? '')
                .slice(0, length)
                .split('');

        if (pastedData?.length) {
          let nextFocusIndex = activeInput;
          const updatedOTPValues = [...otpValues];

          updatedOTPValues.forEach((val, index) => {
            if (pastedData.length > 0) {
              const changedValue = otpDigitsOnly(pastedData.shift() as string);

              if (changedValue) {
                updatedOTPValues[index] = changedValue;
                nextFocusIndex = index;
              }
            }
          });

          setOTPValues(updatedOTPValues);
          setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
          handleOtpChange(updatedOTPValues);
        }
      },
      [activeInput, handleOtpChange, length, otpValues],
    );

    // Handle onChange value for each input
    const handleOnChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = otpDigitsOnly(e.currentTarget.value);
            if (!val) {
                e.preventDefault();
                return;
            }
            if (val.length === length) {
              handleOnPaste(undefined, val.split(''));
              return;
            }
            changeCodeAtFocus(val);
            focusNextInput();
        },
        [changeCodeAtFocus, focusNextInput, handleOnPaste, length],
    );

    /** Blocks junk keys without relying on `key` (Safari often sends `Unidentified` for Arabic digits). */
    const handleBeforeInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
      const ne = e.nativeEvent as InputEvent;
      if (ne.inputType !== 'insertText' || !ne.data || ne.data.length > 1 || ne.isComposing) return;
      if (!otpDigitsOnly(ne.data)) e.preventDefault();
    }, []);

    // Handle onBlur input
    const onBlur = useCallback(() => {
        setActiveInput(-1);
    }, []);

    // Handle onKeyDown input
    const handleOnKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const pressedKey = e.key;

            switch (pressedKey) {
                case 'Backspace':
                case 'Delete': {
                    e.preventDefault();
                    if (otpValues[activeInput]) {
                        changeCodeAtFocus('');
                    } else {
                        focusPrevInput();
                    }
                    break;
                }
                case 'ArrowLeft': {
                    e.preventDefault();
                    focusPrevInput();
                    break;
                }
                case 'ArrowRight': {
                    e.preventDefault();
                    focusNextInput();
                    break;
                }
            }
        },
        [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues],
    );

    return (
        <div {...rest} dir="ltr">
            {Array(length)
                .fill('')
                .map((_, index) => (
                    <SingleInput
                        key={`SingleInput-${index}`}
                        type="text"
                        focus={activeInput === index}
                        value={otpValues?.[index]}
                        autoFocus={autoFocus}
                        onFocus={handleOnFocus(index)}
                        onBeforeInput={handleBeforeInput}
                        onChange={handleOnChange}
                        onKeyDown={handleOnKeyDown}
                        onBlur={onBlur}
                        onPaste={handleOnPaste}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        // maxLength={1}
                        id={`otp-${index}`}
                        style={inputStyle}
                        className={inputClassName}
                        disabled={disabled}
                    />
                ))}			
        </div>
    )
})



export default OTPInput;